import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import toast from 'react-hot-toast';
import BackButtonBottom from '../../components/common/BackButtonBottom';

const REASON_LABELS = {
  roadworks: 'Roadworks / construction',
  closure: 'Road closed',
  diversion: 'Accident / police diversion',
  traffic: 'Severe traffic / delay',
  rider_request: 'Rider asked to change route',
  other: 'Other',
  none_given: 'No reason given',
};

export default function RouteDeviationsReview() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amounts, setAmounts] = useState({});   // bookingId -> amount string
  const [notes, setNotes] = useState({});       // bookingId -> note string
  const [busy, setBusy] = useState(null);       // bookingId being actioned

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fn = httpsCallable(functions, 'getFlaggedDeviations');
      const res = await fn({});
      const list = res.data?.deviations || [];
      setRows(list);
      const seed = {};
      list.forEach((r) => { seed[r.id] = String(r.potentialSurcharge ?? 0); });
      setAmounts(seed);
    } catch (err) {
      toast.error(err.message || 'Could not load deviations');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (bookingId, outcome) => {
    const amount = parseFloat(amounts[bookingId]);
    if ((outcome === 'charge_rider' || outcome === 'compensate_driver') && (!amount || amount <= 0)) {
      toast.error('Enter an amount greater than 0');
      return;
    }
    setBusy(bookingId);
    try {
      const fn = httpsCallable(functions, 'reviewTripDeviation');
      const res = await fn({ bookingId, outcome, amount: amount || 0, notes: notes[bookingId] || '' });
      toast.success(res.data?.message || 'Done');
      setRows((prev) => prev.filter((r) => r.id !== bookingId));
    } catch (err) {
      toast.error(err.message || 'Action failed');
    }
    setBusy(null);
  };

  return (
    <div className="max-w-[800px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/admin')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Admin</button>
      </div>

      <h1 className="font-serif mt-0 mb-1">Route deviations</h1>
      <p className="text-body text-[0.9rem] mb-5">
        Trips that ran further than the booked route. The rider has only been charged the upfront
        quote. Approve a charge or driver compensation only if a genuine reason justifies it.
      </p>

      {loading && <div className="p-10 text-center text-muted">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="p-10 text-center text-muted bg-white rounded-2xl border border-slate-200">
          No deviations awaiting review.
        </div>
      )}

      <div className="grid gap-3">
        {rows.map((r) => {
          const extraKm = (r.actualDistanceKm != null && r.estimatedDistanceKm != null)
            ? Math.round((r.actualDistanceKm - r.estimatedDistanceKm) * 10) / 10
            : null;
          return (
            <div key={r.id} className="p-4 rounded-2xl bg-white border border-slate-200">
              <div className="flex justify-between items-start mb-1.5">
                <div className="font-bold text-brand-dark">{r.referenceNumber || r.id.substring(0, 8)}</div>
                <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold" style={{ background: '#fef3c7', color: '#92400e' }}>
                  Pending review
                </span>
              </div>

              <div className="text-[0.82rem] text-body mb-1">
                {r.pickupAddress || 'Pickup'} → {r.destinationAddress || 'Destination'}
              </div>
              <div className="text-[0.82rem] text-body mb-1">
                Driver: <strong>{r.driverName || r.driverId || '—'}</strong> · Rider: <strong>{r.riderName || r.riderId || '—'}</strong>
              </div>

              <div className="p-2.5 rounded-lg bg-slate-50 my-2 text-[0.82rem] text-body">
                Estimated <strong>{r.estimatedDistanceKm ?? '—'} km</strong> · Actual <strong>{r.actualDistanceKm ?? '—'} km</strong>
                {extraKm != null && <> · <span style={{ color: '#dc2626' }}>+{extraKm} km</span></>}
                <br />
                Quoted fare: <strong>${(r.quotedFare ?? 0).toFixed(2)}</strong> · Would-be surcharge: <strong>${(r.potentialSurcharge ?? 0).toFixed(2)}</strong>
                <br />
                Driver's reason: <strong>{REASON_LABELS[r.deviationReason] || r.deviationReason || 'No reason given'}</strong>
              </div>

              <div className="flex gap-2 items-end flex-wrap mt-2">
                <div>
                  <label className="text-[0.72rem] text-muted block mb-1">Amount ($)</label>
                  <input type="number" min="0" step="0.01" value={amounts[r.id] ?? ''}
                    onChange={(e) => setAmounts((p) => ({ ...p, [r.id]: e.target.value }))}
                    className="w-[90px] px-2 py-1.5 rounded-lg border-[1.5px] border-slate-200 text-[0.9rem]" />
                </div>
                <input type="text" placeholder="Note (optional)" value={notes[r.id] || ''}
                  onChange={(e) => setNotes((p) => ({ ...p, [r.id]: e.target.value }))}
                  className="flex-1 min-w-[140px] px-2 py-1.5 rounded-lg border-[1.5px] border-slate-200 text-[0.9rem]" />
              </div>

              <div className="flex gap-1.5 mt-3 flex-wrap">
                <button onClick={() => act(r.id, 'charge_rider')} disabled={busy === r.id}
                  className="flex-1 min-w-[120px] p-2 rounded-lg border-0 text-white cursor-pointer font-semibold text-[0.8rem]"
                  style={{ background: '#2dd4a8', opacity: busy === r.id ? 0.6 : 1 }}>Charge rider</button>
                <button onClick={() => act(r.id, 'compensate_driver')} disabled={busy === r.id}
                  className="flex-1 min-w-[120px] p-2 rounded-lg border-[1.5px] bg-white cursor-pointer font-semibold text-[0.8rem]"
                  style={{ borderColor: '#4f46e5', color: '#4f46e5', opacity: busy === r.id ? 0.6 : 1 }}>Compensate driver</button>
                <button onClick={() => act(r.id, 'dismiss')} disabled={busy === r.id}
                  className="flex-1 min-w-[100px] p-2 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold text-[0.8rem]"
                  style={{ color: '#ff6b6b', opacity: busy === r.id ? 0.6 : 1 }}>Dismiss</button>
              </div>
            </div>
          );
        })}
      </div>

      <BackButtonBottom to="/admin" label="Admin" />
    </div>
  );
}